import { api } from "./api";
import { rcpsEnabledFor } from "./rcps/enabled";

export const RELATIVE_ENABLED = typeof window !== "undefined" && rcpsEnabledFor(window.location.hostname);
export const MULTIPLES = ["EV/Sales", "EV/EBITDA", "PSR", "PER", "PBR"] as const;
export type Multiple = typeof MULTIPLES[number];
export type Peer = { name: string; symbol: string; consolidated: "자동" | "연결" | "별도"; included: boolean };
export type TargetKey = "revenue" | "operating_income" | "ebitda" | "depreciation" | "net_income" | "equity" | "noa" | "ibd" | "shares_outstanding";
export const TARGET_FIELDS: {key: TargetKey; label: string; hint: string}[] = [
  {key: "revenue", label: "LTM 매출액", hint: "EV/Sales · PSR"},
  {key: "ebitda", label: "LTM EBITDA", hint: "EV/EBITDA · 직접 입력 우선"},
  {key: "operating_income", label: "LTM 영업이익", hint: "EBITDA 미입력 시 감가상각비와 합산"},
  {key: "depreciation", label: "감가상각비·무형자산상각비", hint: "LTM 합계"},
  {key: "net_income", label: "LTM 당기순이익", hint: "PER"},
  {key: "equity", label: "순자산 (자본총계)", hint: "PBR · 재무정보 기준일 잔액"},
  {key: "noa", label: "비영업용자산 (NOA)", hint: "현금 포함 · EV에서 자기자본가치 산출 시 가산"},
  {key: "ibd", label: "이자부부채 (IBD)", hint: "EV에서 자기자본가치 산출 시 차감"},
  {key: "shares_outstanding", label: "발행주식수", hint: "보통주 · 주 단위"},
];
export type Stat = {count: number; median?: number; mean?: number; min?: number; max?: number};
export type PeerSummary = {
  market_cap_date: string; financial_date: string; peer_count: number; multiple_keys: Multiple[];
  peers: {name: string; symbol?: string; consolidated: string; error?: string;
    evidence: Record<string, number | Record<string, number> | null>;
    multiples: Partial<Record<Multiple, number | null>>}[];
  stats: Partial<Record<Multiple, Stat>>;
};
export type RelativeResult = {
  meta: {target_name: string; market_cap_date: string; financial_date: string; central_stat: string};
  peer_multiples: PeerSummary;
  valuation_range: {per_share_low?: number; per_share_central?: number; per_share_high?: number; method_count?: number};
  methods: {key: string; label: string; basis_label: string; basis_value: number; bridge: string;
    multiple_central: number; multiple_low: number; multiple_high: number; equity_central: number;
    per_share_central: number; per_share_low: number; per_share_high: number}[];
  notes: string[];
};
export type Draft = {
  targetName: string; marketDate: string; financialDate: string; financialTouched: boolean;
  peers: Peer[]; multiples: Multiple[]; centralStat: "median" | "mean"; excludeNonpositive: boolean;
  target: Record<TargetKey, string>; source?: string; seedId?: string; sourceUrl?: string; omitted?: number;
};
export function quarterEnd(day: string): string {
  if (!validDate(day)) return "";
  const year = Number(day.slice(0, 4));
  return [`${year}-12-31`, `${year}-09-30`, `${year}-06-30`, `${year}-03-31`, `${year-1}-12-31`].find(d => d <= day)!;
}
export function validDate(day: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(day)) && new Date(day).toISOString().slice(0,10) === day;
}
export function initialDraft(): Draft {
  const today = new Date().toLocaleDateString("en-CA");
  return {targetName: "", marketDate: today, financialDate: quarterEnd(today), financialTouched: false,
    peers: [], multiples: [...MULTIPLES], centralStat: "mean", excludeNonpositive: true,
    target: {revenue: "", operating_income: "", ebitda: "", depreciation: "", net_income: "", equity: "", noa: "0", ibd: "0", shares_outstanding: ""}};
}
export function normalizePeers(rows: Record<string, unknown>[]): {peers: Peer[]; omitted: number} {
  const peers: Peer[] = []; let omitted = 0;
  for (const r of rows) {
    if (r.selected === false) continue;
    const name = String(r.name || r.company_name || r.corp_name || "").trim();
    let symbol = String(r.symbol || r.ticker || r.stock_code || r.code || "").trim();
    if (/^\d{6}$/.test(symbol)) symbol = `KRX:${symbol}`;
    if (r.market === "us" || (symbol && !/^KRX:\d{6}$/.test(symbol)) || (!symbol && !name)) { omitted++; continue; }
    if (peers.some(p => symbol ? p.symbol === symbol : !p.symbol && p.name === name)) continue;
    peers.push({name: name || symbol, symbol, consolidated: "자동", included: true});
  }
  return {peers, omitted};
}
export function manualPeer(name: string, code: string): Peer {
  name = name.trim(); code = code.trim().toUpperCase();
  if (!name && !code) throw new Error("회사명 또는 종목코드를 입력해 주세요.");
  if (code && !/^(KRX:)?\d{6}$/.test(code)) throw new Error("종목코드는 6자리 숫자 또는 KRX:005930 형식으로 입력해 주세요.");
  const symbol = code ? (code.startsWith("KRX:") ? code : `KRX:${code}`) : "";
  return {name:name || symbol, symbol, consolidated:"자동", included:true};
}
/** Group only the integer part, preserving signs, decimal drafts and numeric precision. */
export function formatFinancialInput(value: string): string {
  const raw = value.replaceAll(",", "");
  if (!/^[+-]?\d*(?:\.\d*)?$/.test(raw)) return value;
  const [integer, fraction] = raw.split(".");
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (fraction === undefined ? "" : `.${fraction}`);
}
/** Append discovered companies while preserving each existing peer's inclusion and financial statement settings. */
export function mergeRelativePeers(current: Peer[], incoming: Peer[]): Peer[] {
  const peers = current.map(peer => ({...peer}));
  for (const peer of incoming) {
    const index = peers.findIndex(p => (peer.symbol && p.symbol === peer.symbol) ||
      ((!p.symbol || !peer.symbol) && p.name === peer.name));
    if (index < 0) peers.push({...peer});
    else if (!peers[index].symbol && peer.symbol) peers[index] = {...peers[index], name: peer.name, symbol: peer.symbol};
  }
  return peers;
}
export function peerPayload(d: Draft) {
  if (!validDate(d.marketDate) || !validDate(d.financialDate)) throw new Error("시가총액·재무정보 기준일을 확인해 주세요.");
  if (d.financialDate > d.marketDate) throw new Error("재무정보 기준일은 시가총액 기준일 이전으로 설정해 주세요.");
  const peers = d.peers.filter(p => p.included).map(p => ({name:p.name, ...(p.symbol ? {symbol:p.symbol} : {}), consolidated:p.consolidated}));
  if (!peers.length) throw new Error("평가에 반영할 유사기업을 한 곳 이상 선택해 주세요.");
  if (!d.multiples.length) throw new Error("적용할 배수를 한 개 이상 선택해 주세요.");
  return {peers, market_cap_date:d.marketDate, financial_date:d.financialDate, multiples:d.multiples,
    report_type: "IFRS", exclude_outliers:d.excludeNonpositive};
}
export function peerKey(d: Draft): string {
  return JSON.stringify([d.marketDate, d.financialDate, d.peers.filter(p=>p.included), d.multiples, d.excludeNonpositive]);
}
export function valuationPayload(d: Draft, summary: PeerSummary) {
  const target: Partial<Record<TargetKey, number>> = {};
  for (const {key, label} of TARGET_FIELDS) {
    const s = d.target[key].replaceAll(",", "").trim();
    if (!s) continue;
    const n = Number(s);
    if (!Number.isFinite(n)) throw new Error(`${label}에 올바른 숫자를 입력해 주세요.`);
    target[key] = n;
  }
  const shares = target.shares_outstanding;
  if (!shares || !Number.isSafeInteger(shares) || shares < 0) throw new Error("발행주식수는 1 이상의 정수로 입력해 주세요.");
  if (target.noa == null || target.ibd == null) throw new Error("비영업용자산과 이자부부채를 입력해 주세요. 없으면 0을 입력하세요.");
  if (target.ibd < 0) throw new Error("이자부부채는 0 이상으로 입력해 주세요.");
  const ebitda = target.ebitda ?? (target.operating_income != null ? target.operating_income + (target.depreciation ?? 0) : undefined);
  const basis: Record<Multiple, number | undefined> = {"EV/Sales":target.revenue, "PSR":target.revenue, "EV/EBITDA":ebitda, "PER":target.net_income, "PBR":target.equity};
  if (!d.multiples.some(m => (summary.stats[m]?.count || 0) > 0 && basis[m] != null && (!(m === "PER" || m === "EV/EBITDA") || basis[m]! > 0)))
    throw new Error("산출 가능한 배수에 대응하는 대상회사 재무값을 입력해 주세요.");
  return {...peerPayload(d), target_name:d.targetName.trim() || "평가대상회사", central_stat:d.centralStat, target, peer_multiples_summary:summary};
}
export async function relativeRequest<T>(operation: string, payload: object | FormData): Promise<T> {
  const response = await api<{ok:boolean;data:T;message?:string}>(`/agent/relative/${operation}`, {
    method:"POST", body: payload instanceof FormData ? payload : JSON.stringify({payload}),
  });
  if (!response.ok) throw new Error(response.message || "상대가치평가 요청을 완료하지 못했습니다.");
  return response.data;
}
export function fmtRelative(n: unknown, digits = 1): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("ko-KR", {maximumFractionDigits:digits}) : "—";
}
